import React, { useCallback, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { observer } from 'mobx-react';
import { Table as KSNMTable } from 'ksnm-common-ui/lib/Table2';
import { getLocalRowsByCheckBoxStatus } from 'ksnm-common-ui/lib/Table2/utils/getLocalRowsByCheckBoxStatus';
import { FILTER } from 'ksnm-common-ui/lib/Table2/utils/TableRequest/TableRequest.interface';

import { RoutePath } from '@components/AppRouter/constants';
import { getExcelWithAudit } from '@src/utils/getExcelWithAudit';
import { useTableTitleWithCount } from '@src/utils/hooks/useTableTitleWithCount';
import { useUpdateTable } from '@src/utils/hooks/useUpdateTable';
import { objectFieldsToSnakeCase } from '@src/utils/objectFieldsToCamelCase';
import { constantStore } from '@stores/constantStore';
import { indicatorsStore } from '@stores/dictionaryStores/indicatorsStore/indicatorsStore';
import { ksnmTableStore } from '@stores/ksnmTableStore/ksnmTableStore';

import { ActionButton } from './ActionButton';
import { columns } from './columns';

import styles from './Table.module.scss';

const { TABLE_NAMES } = constantStore;

export const Table = observer(() => {
  const { breadcrumbs, selectNode, indicators, setChecked } = indicatorsStore;

  const { push } = useHistory();

  const [key, update, externalControl] = useUpdateTable({ tableName: TABLE_NAMES.DICTIONARY_INDICATORS });

  useEffect(() => {
    update();
  }, [update, selectNode]);

  const hardFilter = useMemo(() => {
    if (selectNode) {
      if (selectNode.key === 'PCKG') {
        return [
          [
            {
              name: 'rep_vers',
              type: FILTER.EQ,
              values: [null],
              filterIsInverted: false,
            },
          ],
        ];
      }

      const out = [
        {
          name: 'report_group_code',
          type: FILTER.EQ,
          values: [selectNode.reportGroupCode ?? ''],
          filterIsInverted: false,
        },
      ];

      if (selectNode.reportTypeCode) {
        out.push({
          name: 'report_type_code',
          type: FILTER.EQ,
          values: [selectNode.reportTypeCode],
          filterIsInverted: false,
        });
      }

      if (selectNode.repVers) {
        out.push({
          name: 'rep_vers',
          type: FILTER.EQ,
          values: [selectNode.repVers],
          filterIsInverted: false,
        });
      }

      return [out];
    }

    return undefined;
  }, [selectNode]);

  const localData = useMemo(() => indicators.map((item) => objectFieldsToSnakeCase(item)), [indicators]);

  const onCheckBoxChange = useCallback(
    async (tr, sr) => {
      // TODO переделать когда, в таблицном компоненте на беке добавят запрос для получение выбранных строк
      const rows = await getLocalRowsByCheckBoxStatus({
        tableRequest: { ...tr, hardFilter },
        localData: {
          data: localData,
          fieldFormat: {},
        },
        selectRows: sr,
        keyField: 'var_code',
      });

      setChecked(rows);
    },
    [hardFilter, localData, setChecked],
  );

  const onRowClick = useCallback(
    (value) => {
      push(`${RoutePath.DICTIONARY_INDICATORS}/${value?.var_code}`);
    },
    [push],
  );

  const title = useMemo(
    () => (
      <div className={styles.breadcrumbs}>
        {breadcrumbs.map((value) => (
          <div key={value}>{value}</div>
        ))}
      </div>
    ),
    [breadcrumbs],
  );

  const [titleWithCount, onTableChange] = useTableTitleWithCount(title);

  if (!selectNode) return null;

  return (
    <div className={styles.table}>
      <KSNMTable
        externalControl={externalControl}
        tableName={TABLE_NAMES.DICTIONARY_INDICATORS}
        columns={columns}
        tableTitle={titleWithCount}
        resource="ctrl.v_s_var"
        externalFetch={ksnmTableStore.externalFetch}
        externalFetchXLSX={getExcelWithAudit('Справочник "Показатели"')}
        hardFilter={hardFilter}
        onRowClick={onRowClick}
        onCheckBoxChange={onCheckBoxChange}
        extendTableButtons={<ActionButton update={update} />}
        showAggregateRow={false}
        key={key}
        keyField="var_code"
        onTableChange={onTableChange}
      />
    </div>
  );
});
